import { useEffect, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ALL_PAYMENT_METHODS } from "../lib/i18n";
import { USD_TO_IQD } from "../contexts/CurrencyContext";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, Wallet as WalletIcon, Lock, Copy, QrCode } from "lucide-react";

const QR_ENDPOINT = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=4&data=";

export default function Wallet() {
  const { t, lang } = useLang();
  const { fmt, currency } = useCurrency();
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState({
    balance: 0, coins: 0, transactions: [], can_withdraw: false,
    min_balance_after_withdraw: 5,
    platform_zaincash_number: "", platform_superqi_number: "", platform_superqi_card: "",
    okx_trx_address: "", okx_usdt_address: "", okx_network: "Tron (TRC20)",
  });

  // Currency-aware amount conversion (UI ↔ USD for API)
  const toUSD = (val) => currency === "IQD" ? (Number(val) / USD_TO_IQD) : Number(val);
  const fromUSD = (usd) => currency === "IQD" ? Math.round(Number(usd) * USD_TO_IQD) : Number(usd).toFixed(2);
  const curSymbol = currency === "IQD" ? "د.ع" : "$";

  // Free choice — no country restriction (all 4 methods globally enabled)
  const methods = ALL_PAYMENT_METHODS;
  // Withdrawals: all 4 methods globally (no geo restriction)
  const withdrawMethods = methods;
  const [depForm, setDepForm] = useState({ amount: "", method: "zaincash" });
  const [wForm, setWForm] = useState({ amount: "", method: "usdt", destination: "" });

  const load = () => api.get("/wallet").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success(t("copied")); };

  const [depStep, setDepStep] = useState(1); // 1=enter amount/method, 2=show wallet number, 3=confirmed
  const [depPending, setDepPending] = useState(null);

  const confirmDeposit = async () => {
    const usdAmount = toUSD(depForm.amount);
    if (!usdAmount || usdAmount <= 0) { toast.error("أدخل مبلغ صحيح"); return; }
    try {
      const { data: dep } = await api.post("/wallet/deposit", { amount: usdAmount, method: depForm.method });
      setDepPending({ ...dep, displayAmount: depForm.amount });
      setDepStep(3);
      toast.success("✓ تم إرسال الطلب — ينتظر مراجعة الأدمن");
      load(); refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const resetDeposit = () => { setDepStep(1); setDepPending(null); setDepForm({ amount: "", method: "zaincash" }); };

  const withdraw = async () => {
    const amtUSD = toUSD(wForm.amount);
    if (data.balance - amtUSD < data.min_balance_after_withdraw) {
      toast.error(`يجب أن يبقى $${data.min_balance_after_withdraw} في الرصيد على الأقل`);
      return;
    }
    try { await api.post("/wallet/withdraw", { amount: amtUSD, method: wForm.method, destination: wForm.destination });
      toast.success(t("withdraw") + " ✓ (قيد المراجعة)"); load(); refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const isCrypto = (m) => m === "trx" || m === "usdt";

  const depositInfo = () => {
    const m = depForm.method;
    if (m === "zaincash") return { label: "زين كاش", address: data.platform_zaincash_number, network: null, extra: null };
    if (m === "super_qi") return { label: "سوبر كي", address: data.platform_superqi_number, network: null, extra: `كارت سوبر كي: ${data.platform_superqi_card}` };
    if (m === "trx")  return { label: "TRX → OKX", address: data.okx_trx_address,  network: data.okx_network, extra: "الحد الأدنى: 1 TRX" };
    if (m === "usdt") return { label: "USDT → OKX", address: data.okx_usdt_address, network: data.okx_network, extra: "الحد الأدنى: 0.01 USDT" };
    return { label: "", address: "", network: null, extra: null };
  };
  const info = depositInfo();

  const maxWithdrawable = Math.max(0, data.balance - data.min_balance_after_withdraw);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient mb-8">{t("wallet")}</h1>

      <div className="bento-card p-8 mb-8 relative overflow-hidden">
        <div className="absolute -top-12 -end-12 w-48 h-48 rounded-full bg-[#00E5FF]/15 blur-3xl" />
        <WalletIcon className="w-8 h-8 text-[#00E5FF] mb-4" />
        <div className="text-sm text-[#8FA3BD]">{t("yourBalance")}</div>
        <div className="text-5xl font-bold text-cyan-gradient mt-2" data-testid="wallet-balance">{fmt(data.balance)}</div>
        <div className="text-sm text-[#8FA3BD] mt-2">{data.coins} {t("coins")}</div>
        <div className="text-xs text-[#4A5970] mt-1">القابل للسحب: <span className="text-[#00E676]">{fmt(maxWithdrawable)}</span> · يبقى ${data.min_balance_after_withdraw} للاستثمار</div>

        <div className="flex flex-wrap gap-3 mt-6">
          <Dialog onOpenChange={(o) => { if (!o) resetDeposit(); }}>
            <DialogTrigger asChild>
              <Button className="btn-cyan" data-testid="open-deposit-btn"><ArrowDownToLine className="w-4 h-4 me-1.5" />{t("deposit")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white max-w-md">
              {depStep !== 3 ? (
              <>
              <DialogHeader>
                <DialogTitle>{t("deposit")}</DialogTitle>
                <DialogDescription className="text-[#8FA3BD]">1. أدخل المبلغ والطريقة → 2. حوّل للرقم → 3. تثبيت الطلب</DialogDescription>
              </DialogHeader>
              <div>
                <Input type="number" placeholder={`${t("amount")} (${curSymbol})`} value={depForm.amount}
                  onChange={(e) => setDepForm({ ...depForm, amount: e.target.value })}
                  className="bg-[#050E1A] border-[#1A2D4D]" data-testid="deposit-amount-input" />
                {depForm.amount > 0 && (
                  <div className="text-[10px] text-[#8FA3BD] mt-1 ms-1">
                    {currency === "IQD" ? `≈ $${toUSD(depForm.amount).toFixed(2)}` : `≈ ${Math.round(Number(depForm.amount) * USD_TO_IQD).toLocaleString()} د.ع`}
                  </div>
                )}
              </div>
              <Select value={depForm.method} onValueChange={(v) => setDepForm({ ...depForm, method: v })}>
                <SelectTrigger className="bg-[#050E1A] border-[#1A2D4D]" data-testid="deposit-method-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
                  {methods.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`dep-method-${m.id}`}>{m.icon} {lang === "ar" ? m.label_ar : m.label_en}</SelectItem>)}
                </SelectContent>
              </Select>
              {info.address && depForm.amount > 0 && (
                <div className="p-4 rounded-lg bg-[#050E1A] border border-[#FF9500]/40 space-y-3">
                  <div className="text-sm font-bold text-[#FF9500] text-center">📤 حوّل {depForm.amount} {curSymbol} (≈ {currency === "IQD" ? `$${toUSD(depForm.amount).toFixed(2)}` : `${Math.round(Number(depForm.amount) * USD_TO_IQD).toLocaleString()} د.ع`}) إلى الرقم التالي ثم اضغط "تثبيت الإيداع"</div>
                  <div className="text-xs text-[#8FA3BD]">إلى: <span className="text-[#00E5FF]">{info.label}</span></div>
                  {isCrypto(depForm.method) && (
                    <div className="flex justify-center">
                      <img src={`${QR_ENDPOINT}${encodeURIComponent(info.address)}`} alt="QR" className="rounded-lg border border-[#1A2D4D] bg-white p-1" data-testid={`qr-${depForm.method}`} />
                    </div>
                  )}
                  <div className="flex items-center gap-2 break-all">
                    <code className="text-[#00E5FF] font-bold text-sm flex-1" data-testid="platform-receive-number">{info.address}</code>
                    <Button size="sm" variant="outline" className="border-[#1A2D4D]" onClick={() => copy(info.address)} data-testid="copy-address-btn"><Copy className="w-3 h-3" /></Button>
                  </div>
                  {info.network && <div className="text-xs text-[#8FA3BD]">الشبكة: <span className="text-[#D4AF37]">{info.network}</span></div>}
                  {info.extra && <div className="text-xs text-[#8FA3BD]">{info.extra}</div>}
                  {isCrypto(depForm.method) && (
                    <div className="text-[10px] text-[#FF9500] border-t border-[#1A2D4D] pt-2">⚠ أرسل فقط عبر شبكة TRC20.</div>
                  )}
                </div>
              )}
              <DialogFooter><Button onClick={confirmDeposit} disabled={!depForm.amount || Number(depForm.amount) <= 0} className="btn-cyan w-full" data-testid="confirm-deposit-btn">تثبيت الإيداع (ينتظر موافقة الأدمن)</Button></DialogFooter>
              </>
              ) : (
              <>
              <DialogHeader>
                <DialogTitle className="text-[#00E676]">✓ تم تثبيت طلبك</DialogTitle>
                <DialogDescription className="text-[#8FA3BD]">رقم الطلب: <code className="text-[#00E5FF]">{depPending?.deposit_id}</code></DialogDescription>
              </DialogHeader>
              <div className="p-4 rounded-lg bg-[#00E676]/10 border border-[#00E676]/30 text-sm space-y-2">
                <div>📤 المبلغ: <span className="text-[#00E5FF] font-bold">{fmt(depPending?.amount || 0)}</span></div>
                <div>📡 الطريقة: <span className="text-[#00E5FF]">{depPending?.method}</span></div>
                <div>⏳ الحالة: <span className="text-[#FF9500]">قيد المراجعة</span></div>
                <div className="text-xs text-[#8FA3BD] border-t border-[#1A2D4D] pt-2 mt-3">سيصلك إشعار بمجرد قبول الأدمن، وسيُضاف المبلغ لرصيدك تلقائياً.</div>
              </div>
              <DialogFooter><Button onClick={resetDeposit} className="btn-cyan w-full">حسناً</Button></DialogFooter>
              </>
              )}
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#1A2D4D] text-white" data-testid="open-withdraw-btn" disabled={!data.can_withdraw}>
                {data.can_withdraw ? <ArrowUpFromLine className="w-4 h-4 me-1.5" /> : <Lock className="w-4 h-4 me-1.5" />}
                {t("withdraw")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
              <DialogHeader>
                <DialogTitle>{t("withdraw")}</DialogTitle>
                <DialogDescription className="text-[#8FA3BD]">القابل للسحب: ${maxWithdrawable.toFixed(2)} (يبقى ${data.min_balance_after_withdraw})</DialogDescription>
              </DialogHeader>
              <div className="p-3 rounded-lg bg-[#FF9500]/10 border border-[#FF9500]/30 text-xs text-[#FF9500] text-center">
                📅 السحوبات تُعالج <b>يوم الخميس فقط</b>
              </div>
              <div>
                <Input type="number" placeholder={`${t("amount")} (${curSymbol})`} value={wForm.amount}
                  onChange={(e) => setWForm({ ...wForm, amount: e.target.value })}
                  className="bg-[#050E1A] border-[#1A2D4D]" data-testid="withdraw-amount-input" />
                {wForm.amount > 0 && (
                  <div className="text-[10px] text-[#8FA3BD] mt-1 ms-1">
                    {currency === "IQD" ? `≈ $${toUSD(wForm.amount).toFixed(2)}` : `≈ ${Math.round(Number(wForm.amount) * USD_TO_IQD).toLocaleString()} د.ع`}
                  </div>
                )}
              </div>
              <Select value={wForm.method} onValueChange={(v) => setWForm({ ...wForm, method: v })}>
                <SelectTrigger className="bg-[#050E1A] border-[#1A2D4D]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F1F35] border-[#1A2D4D] text-white">
                  {withdrawMethods.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`w-method-${m.id}`}>{m.icon} {lang === "ar" ? m.label_ar : m.label_en}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-[#FF9500] bg-[#FF9500]/5 border border-[#FF9500]/30 p-2 rounded">
                💡 يمكنك السحب عبر زين كاش، سوبر كي، USDT أو TRX (TRC20)
              </div>
              <Input placeholder={
                  wForm.method === "zaincash" ? "رقم زين كاش (07XXXXXXXXX)"
                : wForm.method === "super_qi" ? "رقم سوبر كي أو كارت سوبر كي"
                : "TRX/USDT wallet address (TRC20)"
                }
                value={wForm.destination} onChange={(e) => setWForm({ ...wForm, destination: e.target.value })}
                className="bg-[#050E1A] border-[#1A2D4D]" data-testid="withdraw-dest-input" />
              <DialogFooter><Button onClick={withdraw} className="btn-cyan w-full" data-testid="confirm-withdraw-btn">{t("submit")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {!data.can_withdraw && (
          <div className="text-xs text-[#FF9500] mt-3 flex items-center gap-1.5"><Lock className="w-3 h-3" /> {t("withdrawLocked")}</div>
        )}
      </div>

      <h2 className="text-lg font-semibold mb-4">{t("transactions")}</h2>
      <div className="bento-card p-2 table-scroll">
        <table className="w-full text-sm">
          <thead className="text-xs text-[#8FA3BD]">
            <tr>
              <th className="p-3 text-start">Order ID</th>
              <th className="p-3 text-start">Type</th>
              <th className="p-3 text-start">Method</th>
              <th className="p-3 text-start">{t("amount")}</th>
              <th className="p-3 text-start">Status</th>
              <th className="p-3 text-start">Date</th>
            </tr>
          </thead>
          <tbody>
            {data.transactions.map((tx) => (
              <tr key={tx.tx_id} className="border-t border-[#1A2D4D]" data-testid={`tx-${tx.tx_id}`}>
                <td className="p-3 text-[10px] text-[#00E5FF] font-mono">{tx.order_id || "—"}</td>
                <td className="p-3">{tx.type.replace(/_/g, " ")}</td>
                <td className="p-3 text-[#8FA3BD]">{tx.method}</td>
                <td className={`p-3 font-medium ${tx.amount > 0 ? "text-[#00E676]" : "text-[#8FA3BD]"}`}>{fmt(tx.amount, { sign: true })}</td>
                <td className="p-3 text-xs text-[#4A5970]">{tx.status}</td>
                <td className="p-3 text-[10px] text-[#4A5970]">{(tx.created_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {data.transactions.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#4A5970]">—</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
